import {
  AmbientLight,
  Box3,
  MeshPhongMaterial,
  PerspectiveCamera,
  PointLight,
  Scene,
  Sphere,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// three.js ships types for the public examples/jsm API (via @types/three) but not for this
// internal collada/ subfolder. A local `declare module` augmentation doesn't work here either -
// TypeScript resolves the .js file on disk regardless of allowJs, so it treats the module as
// already-existing-but-untyped and refuses to let anything "augment" it (TS2665). Suppressing the
// single diagnostic below leaves the import as an implicit `any`, which is exactly what
// buildMaterial(data: any) below needs anyway.
// @ts-expect-error - ColladaComposer.js is an internal three.js module with no shipped types.
import { ColladaComposer } from 'three/examples/jsm/loaders/collada/ColladaComposer.js';
import { fittedDistanceForAspect } from './scene-geometry.util';

// The 3D preview's scene/camera/lights/renderer/controls setup, shared by the beta designer
// (design-3d-redesign.component.ts) and the redesigned Upload Your Design page so both show the
// box with exactly the same look and behavior. Everything here was lifted verbatim out of the beta
// designer's initScene()/fitCameraToAvatar()/loadBox() - change it here, not in a copy.

// three.js's ColladaComposer.buildMaterial() throws a plain, unguarded TypeError when a material's
// sampler references a surface id that doesn't resolve (a broken/dangling reference inside Box.dae,
// not just a texture file 404ing over the network - see sweepBrokenTextures() below for that other
// case). ColladaLoader.load() catches that exception at the top level and treats it as the WHOLE
// Box.dae failing to load - even though only one material's reference is actually broken, and even
// when that material is a cosmetic one (e.g. edges) with no bearing on whether the box is usable.
// Patched once per module load (idempotent), so a single broken material reference degrades to a
// plain fallback material instead of taking down parsing for the whole box.
let colladaFallbackInstalled = false;
export function installColladaMaterialFallback(): void {
  if (colladaFallbackInstalled) {
    return;
  }
  colladaFallbackInstalled = true;
  const originalBuildMaterial = ColladaComposer.prototype.buildMaterial;
  ColladaComposer.prototype.buildMaterial = function (data: any) {
    try {
      return originalBuildMaterial.call(this, data);
    } catch (e) {
      console.error(`3D preview: material "${data?.name}" has a broken texture reference - using a plain fallback.`, e);
      const fallback = new MeshPhongMaterial();
      fallback.name = data?.name || '';
      return fallback;
    }
  };
}

export interface PreviewRig {
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  controls: OrbitControls;
}

// Builds the camera, lights, renderer and orbit controls into `scene`, and mounts the renderer's
// canvas in `container`. Sized off the container's own size (falling back to 500px tall when the
// container has no height yet).
export function createPreviewRig(container: HTMLElement, scene: Scene, category?: string): PreviewRig {
  const width = container.clientWidth;
  const height = container.clientHeight || 500;

  const camera = new PerspectiveCamera(5, width / height, 1, 1000);
  camera.position.set(4.5, 2, 0);

  // Intensities tuned for three.js's physically-correct light units (default since r155):
  // point-light irradiance falls off with distance^2, so matching the old pre-r155 apparent
  // brightness at this camera distance (~4.5 units) needs a much higher raw intensity than the
  // non-physical 0.2/0.8 values the old upload preview used.
  const ambientLight = new AmbientLight(0xfefefe, 1);
  const pointLight = new PointLight(0xfefefe, 15);
  scene.add(ambientLight);
  scene.add(camera);
  camera.add(pointLight);

  const renderer = new WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = true;
  renderer.setClearColor(0xefefef);
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableZoom = true;
  controls.minZoom = 0.5;
  controls.maxZoom = 2;
  controls.screenSpacePanning = true;
  controls.minDistance = 1;
  controls.maxDistance = 5;
  // Locked to a single fixed pivot: panning (which would otherwise drag the orbit target off the
  // box) is disabled.
  controls.enablePan = false;

  // Event Boxes keep their own wider FOV as a deliberate look (more dramatic perspective) - but
  // not their own fixed camera distance/min/maxDistance any more, since fitPreviewCamera() (once
  // the real box geometry exists) derives those for every category from the model's actual size.
  if (category === 'Event Boxes') {
    controls.minZoom = 1;
    controls.maxZoom = 5;
    camera.fov = 20;
  }
  controls.update();

  return { camera, renderer, controls };
}

// The y-offset the loaded box is shifted by so it sits centered in the frame.
export function previewModelYOffset(category?: string): number {
  return category === 'Event Boxes' ? -0.4 : -0.05;
}

// A fixed camera distance only looks right for the one nominal box size it was eyeballed against -
// a much larger box just fills more of the same frustum, reading as "too zoomed in". Measuring the
// loaded model's real bounding sphere and deriving distance from it fits any box size.
//
// Moves the orbit target to the sphere's center, dollies the camera to the fitted distance along
// its current direction, and scales the zoom range off that distance. Returns the measurements so
// the caller can keep them as its own "default view" baseline; undefined when the model has no
// measurable size.
export function fitPreviewCamera(
  camera: PerspectiveCamera,
  controls: OrbitControls,
  avatar: any,
): { radius: number; distance: number; center: Vector3 } | undefined {
  const box = new Box3().setFromObject(avatar);
  const sphere = new Sphere();
  box.getBoundingSphere(sphere);
  if (!Number.isFinite(sphere.radius) || sphere.radius <= 0) {
    return undefined;
  }

  const distance = fittedDistanceForAspect(camera.fov, camera.aspect, sphere.radius);
  const direction = camera.position.clone().sub(controls.target).normalize();
  camera.position.copy(sphere.center).addScaledVector(direction, distance);
  controls.target.copy(sphere.center);

  controls.minDistance = distance * 0.4;
  controls.maxDistance = distance * 2.2;
  controls.update();

  return { radius: sphere.radius, distance, center: sphere.center.clone() };
}

// Finds the real material name a Face mesh actually uses, by prefix ("0Outside"/"1Inside") -
// guessing the exact name from route params isn't reliable (observed as e.g. "sece" against a
// material actually named "0OutsideSECE662"). A mesh's .material can be a single material or an
// array of them, so both forms are checked.
export function findMaterialNameByPrefix(avatar: any, prefix: string): string | null {
  let found: string | null = null;
  avatar.traverse((child: any) => {
    if (found || !child.isMesh) {
      return;
    }
    const candidates = Array.isArray(child.material) ? child.material : [child.material];
    const match = candidates.find((m: any) => typeof m?.name === 'string' && m.name.startsWith(prefix));
    if (match) {
      found = match.name;
    }
  });
  return found;
}

// Runs once every sub-resource Box.dae's parse started (chiefly baked material textures) has
// either loaded or failed - hook it to the LoadingManager's onLoad. A material left with a failed
// load has map.image still unset (three.js never assigns it on error) - dropping the map falls
// that material back to its own base color instead of rendering blank/black.
export function sweepBrokenTextures(materials: Record<string, any>): void {
  Object.entries(materials).forEach(([name, material]) => {
    const map = material?.build?.map;
    if (map && !map.image) {
      console.error(`3D preview: texture for material "${name}" failed to load - falling back to its base color.`);
      material.build.map = null;
      material.build.needsUpdate = true;
    }
  });
}

// The camera bearing/elevation the beta designer opens on, for a box whose front faces `bearing`
// (horizontal). Split out of computeDefaultPanelDirection() there so a page with no per-panel
// data (the upload preview) can open on the same 3/4 angle.
export { elevatedDefaultDirection } from './scene-geometry.util';
